import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { subscriptionAPI } from 'redux/subscriptionAPI';
import { invoiceAPI } from 'redux/invoiceAPI';
import { PayerError } from '@payer-admin-ui/components';
import { patch } from '@payer-admin-ui/components-v2/src/functions/lodashExtensions';

export const createSubscription = createAsyncThunk('subscriptions/createSubscription', async (_, { getState }) => {
  const {
    subscription: {
      externalReferenceId,
      periodTime,
      periodTimeUnit,
      startDate,
      endDate,
      deliveryType,
      subscriptionPaymentMethod,
      invoiceConfig,
      templateOrder: {
        additionalReferenceId,
        description,
        comment,
        currencyCode,
        yourReference,
        invoiceCustomer,
        items,
        referenceId,
      },
      storeCardConfig,
    },
  } = getState().subscriptions.createSubscription;
  const subscription = {
    orderData: {
      clientIpAddress: null, // String?
      referenceId, // String
      additionalReferenceId, // String?
      description, // String
      comment, // String?
      currencyCode, // CurrencyCode
      currencyRate: null, // String?
      purchaseChannel: null, // PurchaseChannel?
      site: null, // String?
      uri: null, // String?
      ourReference: null, // String?
      yourReference, // String?
      yourOrderNumber: null, // String?
      project: null, // String?
      invoiceCustomer, // OrderCustomer
      deliveryCustomer: null, // OrderCustomer?
      orderingCustomer: null, // OrderCustomer?
      sellingCustomer: null, // OrderCustomer?
      items, // List<LineItem>
      metaData: null, // List<OrderMetaData>?
    },
    subscriptionConfig: {
      externalReferenceId,
      deliveryType,
      periodTimeUnit,
      periodTime,
      startDate,
      endDate,
    },
  };
  if (subscriptionPaymentMethod === 'CARD') {
    subscription['storeCardConfig'] = storeCardConfig;
    return subscriptionAPI.createOrderAndCardSubscription(subscription);
  }
  if (subscriptionPaymentMethod === 'INVOICE') {
    subscription['invoiceConfig'] = invoiceConfig;
    return subscriptionAPI.createOrderAndInvoiceSubscription(subscription);
  }
  throw new PayerError({ code: 'subscriptionPaymentMethod' });
});

export const getAddress = createAsyncThunk('subscriptions/getAddress', async (_, { getState }) => {
  const {
    regNumber,
    address: { countryCode },
  } = getState().subscriptions.createSubscription.subscription.templateOrder.invoiceCustomer;
  return invoiceAPI.getAddress({
    countryCode,
    regNumber,
  });
});

const initialState = {
  subscription: {
    id: null,
    externalReferenceId: null,
    periodTime: '1',
    periodTimeUnit: 'MONTH',
    startDate: null,
    endDate: null,
    deliveryType: 'EMAIL',
    subscriptionPaymentMethod: 'INVOICE',
    status: null,
    numberOfBillings: null,
    cardToken: null,
    invoiceConfig: {
      type: 'NORMAL',
      disableReminders: false,
      dueDays: '30',
      paymentMethods: ['BANK'],
    },
    templateOrder: {
      clientIpAddress: null,
      createDate: null,
      orderId: null,
      referenceId: null,
      additionalReferenceId: null,
      status: null,
      description: null,
      comment: null,
      currencyCode: 'SEK',
      currencyRate: null,
      purchaseChannel: null,
      site: null,
      uri: null,
      ourReference: null,
      yourReference: null,
      project: null,
      invoiceCustomer: {
        customerNumber: null,
        customerType: 'ORGANISATION',
        regNumber: null,
        address: {
          companyName: null,
          yourReference: null,
          title: null,
          firstName: null,
          lastName: null,
          streetAddress1: null,
          streetAddress2: null,
          coAddress: null,
          houseAddress: null,
          zipCode: null,
          city: null,
          state: null,
          countryCode: 'SE',
          languageCode: null,
          phoneNumber: null,
          emailAddress: null,
          emailAddressCc: null,
          emailAddressBcc: null,
        },
        customerVatId: null,
        websiteUrl: null,
      },
      deliveryCustomer: null,
      orderingCustomer: null,
      sellingCustomer: null,
      items: [],
      metaData: [],
      approveDate: null,
    },
    storeCardConfig: {
      backToShopURL: 'https://www.payer.eu',
      redirectOnSuccessURL: 'https://www.payer.eu',
      redirectOnFailURL: 'https://www.payer.eu',
    },
  },
  createdSubscriptionId: null,
  cardWidgetURL: null,
  addressFetchState: { isFetching: false, success: false },
  submit: { isFetching: false, success: false },
  error: null,
};

const createSubscriptionSlice = createSlice({
  name: 'subscriptions/create',
  initialState,
  reducers: {
    resetError: state => ({ ...state, error: null }),
    reset: _state => ({ ...initialState }),
    patchSubscription: (state, { payload: subscriptionPatch }) => {
      patch(state.subscription, subscriptionPatch);
    },
    resetGetAddressSuccess: state => {
      state.addressFetchState.success = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(createSubscription.pending, (state, _) => ({
      ...state,
      submit: { isFetching: true, success: false },
      error: null,
    }));
    builder.addCase(
      createSubscription.fulfilled,
      (
        state,
        {
          payload: {
            cardWidgetUrl,
            subscription: { id },
          },
        },
      ) => ({
        ...state,
        submit: {
          isFetching: false,
          success: true,
        },
        createdSubscriptionId: id,
        cardWidgetURL: cardWidgetUrl,
      }),
    );
    builder.addCase(createSubscription.rejected, (state, { error }) => ({
      ...state,
      submit: { isFetching: false },
      error,
    }));

    builder.addCase(getAddress.pending, (state, _) => ({
      ...state,
      addressFetchState: { isFetching: true, success: false },
      error: null,
    }));
    builder.addCase(
      getAddress.fulfilled,
      (
        state,
        {
          payload: {
            companyName,
            yourReference,
            firstName,
            lastName,
            streetAddress1,
            streetAddress2,
            zipCode,
            city,
            countryCode,
          },
        },
      ) =>
        patch(state, {
          subscription: {
            templateOrder: {
              invoiceCustomer: {
                address: {
                  companyName: companyName || null,
                  yourReference: yourReference || null,
                  firstName: firstName || null,
                  lastName: lastName || null,
                  streetAddress1: streetAddress1 || null,
                  streetAddress2: streetAddress2 || null,
                  zipCode: zipCode || null,
                  city: city || null,
                  countryCode: countryCode || null,
                },
              },
            },
          },
          addressFetchState: { isFetching: false, success: true },
        }),
    );
    builder.addCase(getAddress.rejected, (state, { error }) => ({
      ...state,
      addressFetchState: { isFetching: false },
      error,
    }));
  },
});

export const {
  actions: { resetError, reset, resetGetAddressSuccess, patchSubscription },
  reducer: createSubscriptionReducer,
} = createSubscriptionSlice;
